import { useContext, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Alert,
  Tooltip,
  IconButton,
  Chip,
  alpha,
  Paper,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory2';
import EditIcon from '@mui/icons-material/Edit';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import StoreIcon from '@mui/icons-material/Store';
import axiosInstance from '../../../lib/axios';
import type { Inventory, ProductVariant, Location } from '../../../types';
import { ColorModeContext } from '../../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../../theme/colors';
import { QuickStockAdjustmentDialog } from './QuickStockAdjustmentDialog';

interface InventoryWithLocation extends Inventory {
  locationName?: string;
  variantId?: string;
}

interface VariantInventoryTabProps {
  productId: string;
}

const fetchVariants = async (productId: string): Promise<ProductVariant[]> => {
  const response = await axiosInstance.get(`/products/${productId}/variants`);
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchLocations = async (): Promise<Location[]> => {
  const response = await axiosInstance.get('/locations');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchVariantInventory = async (productId: string): Promise<InventoryWithLocation[]> => {
  // Fetch all inventory records for all variants of this product
  // Note: This endpoint should be created or we fetch per variant
  const response = await axiosInstance.get(`/inventory/product/${productId}/variants`);
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

export const VariantInventoryTab = ({ productId }: VariantInventoryTabProps) => {
  const { mode } = useContext(ColorModeContext);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>();
  const [selectedLocationId, setSelectedLocationId] = useState<string | undefined>();

  const { data: variants = [], isLoading: variantsLoading, error: variantsError } = useQuery({
    queryKey: ['variants', productId],
    queryFn: () => fetchVariants(productId),
    enabled: !!productId,
  });

  const { data: locations = [], isLoading: locationsLoading, error: locationsError } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocations,
  });

  const { data: inventory = [], isLoading: inventoryLoading, error: inventoryError } = useQuery({
    queryKey: ['variantInventory', productId],
    queryFn: () => fetchVariantInventory(productId),
    enabled: !!productId,
  });

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const isLoading = variantsLoading || locationsLoading || inventoryLoading;
  const error = variantsError || locationsError || inventoryError;

  const handleCellClick = (variantId: string, locationId: string) => {
    setSelectedVariantId(variantId);
    setSelectedLocationId(locationId);
    setStockDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setStockDialogOpen(false);
    setSelectedVariantId(undefined);
    setSelectedLocationId(undefined);
  };

  // Create a lookup map: variantId -> locationId -> inventory
  const inventoryMap = new Map<string, Map<string, InventoryWithLocation>>();
  inventory.forEach((inv) => {
    const variantId = inv.variantId || inv.productId; // Fallback to productId if variantId not set
    if (!inventoryMap.has(variantId)) {
      inventoryMap.set(variantId, new Map());
    }
    inventoryMap.get(variantId)!.set(inv.locationId, inv);
  });

  // Calculate totals
  const totalQuantity = inventory.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const totalReserved = inventory.reduce((sum, item) => sum + (item.reserved || 0), 0);
  const totalAllocated = inventory.reduce((sum, item) => sum + (item.allocated || 0), 0);
  const totalAvailable = totalQuantity - totalReserved - totalAllocated;

  // Format attributes for display
  const formatAttributes = (attributes: Record<string, unknown>): string => {
    return Object.entries(attributes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  // Get stock status color
  const getStockColor = (quantity: number, threshold: number = 10): string => {
    if (quantity === 0) return palette.error;
    if (quantity <= threshold) return palette.warning;
    return palette.success;
  };

  return (
    <>
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: theme.radius.md,
        }}
      >
        <CardContent>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InventoryIcon sx={{ color: palette.primary, fontSize: 28 }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: typography.fontFamily.display,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.textPrimary,
                  textTransform: 'uppercase',
                }}
              >
                Variant Inventory
              </Typography>
            </Box>
          </Box>

          {/* Total Inventory Summary */}
          {!isLoading && !error && inventory.length > 0 && (
            <Box
              sx={{
                p: 2,
                mb: 3,
                borderRadius: theme.radius.sm,
                backgroundColor: alpha(palette.primary, 0.05),
                border: `2px solid ${palette.primary}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.bold,
                  textTransform: 'uppercase',
                  letterSpacing: typography.letterSpacing.widest,
                  color: colors.textMuted,
                  mb: 1,
                  display: 'block',
                }}
              >
                Total Across All Variants & Locations
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      color: colors.textMuted,
                      fontSize: typography.fontSize.xs,
                    }}
                  >
                    Total Quantity
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.extrabold,
                      color: palette.primary,
                    }}
                  >
                    {totalQuantity}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      color: colors.textMuted,
                      fontSize: typography.fontSize.xs,
                    }}
                  >
                    Available
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.extrabold,
                      color: totalAvailable > 0 ? palette.success : palette.error,
                    }}
                  >
                    {totalAvailable}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      color: colors.textMuted,
                      fontSize: typography.fontSize.xs,
                    }}
                  >
                    Reserved
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.extrabold,
                      color: palette.warning,
                    }}
                  >
                    {totalReserved}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      color: colors.textMuted,
                      fontSize: typography.fontSize.xs,
                    }}
                  >
                    Allocated
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.extrabold,
                      color: palette.info,
                    }}
                  >
                    {totalAllocated}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Loading State */}
          {isLoading && (
            <Box>
              <Skeleton variant="rectangular" height={60} sx={{ mb: 2, borderRadius: theme.radius.sm }} />
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: theme.radius.sm }} />
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ fontFamily: typography.fontFamily.mono }}>
              {error instanceof Error ? error.message : 'Failed to load inventory data'}
            </Alert>
          )}

          {/* Empty State */}
          {!isLoading && !error && variants.length === 0 && (
            <Box
              sx={{
                py: 6,
                textAlign: 'center',
                borderRadius: theme.radius.md,
                border: `2px dashed ${colors.borderLight}`,
              }}
            >
              <InventoryIcon sx={{ fontSize: 48, color: colors.textMuted, mb: 2 }} />
              <Typography
                variant="body1"
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  color: colors.textMuted,
                }}
              >
                No variants found. Create variants to track inventory.
              </Typography>
            </Box>
          )}

          {/* Inventory Matrix Table */}
          {!isLoading && !error && variants.length > 0 && (
            <TableContainer
              component={Paper}
              sx={{
                backgroundColor: 'transparent',
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                overflow: 'auto',
                maxHeight: 600,
              }}
            >
              <Table stickyHeader sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.bold,
                        textTransform: 'uppercase',
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.wide,
                        color: colors.textMuted,
                        backgroundColor: colors.surfaceElevated,
                        borderBottom: `2px solid ${colors.border}`,
                        position: 'sticky',
                        left: 0,
                        zIndex: 3,
                      }}
                    >
                      Variant
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.bold,
                        textTransform: 'uppercase',
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.wide,
                        color: colors.textMuted,
                        backgroundColor: colors.surfaceElevated,
                        borderBottom: `2px solid ${colors.border}`,
                        position: 'sticky',
                        left: 200,
                        zIndex: 3,
                      }}
                    >
                      SKU
                    </TableCell>
                    {locations.map((location) => (
                      <TableCell
                        key={location.id}
                        align="center"
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          fontSize: typography.fontSize.xs,
                          letterSpacing: typography.letterSpacing.wide,
                          color: colors.textMuted,
                          backgroundColor: colors.surfaceElevated,
                          borderBottom: `2px solid ${colors.border}`,
                          minWidth: 120,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          {location.type === 'warehouse' ? (
                            <WarehouseIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <StoreIcon sx={{ fontSize: 16 }} />
                          )}
                          {location.name}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {variants.map((variant) => {
                    const variantInventory = inventoryMap.get(variant.id) || new Map();

                    return (
                      <TableRow
                        key={variant.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(palette.primary, 0.05),
                          },
                        }}
                      >
                        {/* Variant Info - Sticky */}
                        <TableCell
                          sx={{
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.sm,
                            color: colors.textPrimary,
                            borderBottom: `1px solid ${colors.borderLight}`,
                            position: 'sticky',
                            left: 0,
                            backgroundColor: colors.surface,
                            zIndex: 2,
                            maxWidth: 200,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: typography.fontFamily.mono,
                              fontSize: typography.fontSize.xs,
                              color: colors.textSecondary,
                              mb: 0.5,
                            }}
                          >
                            {formatAttributes(variant.attributes)}
                          </Typography>
                          <Chip
                            label={variant.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{
                              fontFamily: typography.fontFamily.mono,
                              fontSize: typography.fontSize.xs,
                              fontWeight: typography.fontWeight.bold,
                              backgroundColor: variant.isActive
                                ? alpha(palette.success, 0.1)
                                : alpha(palette.error, 0.1),
                              color: variant.isActive ? palette.success : palette.error,
                              border: `1px solid ${variant.isActive ? palette.success : palette.error}`,
                            }}
                          />
                        </TableCell>

                        {/* SKU - Sticky */}
                        <TableCell
                          sx={{
                            fontFamily: typography.fontFamily.mono,
                            fontSize: typography.fontSize.sm,
                            fontWeight: typography.fontWeight.bold,
                            color: colors.textPrimary,
                            borderBottom: `1px solid ${colors.borderLight}`,
                            position: 'sticky',
                            left: 200,
                            backgroundColor: colors.surface,
                            zIndex: 2,
                          }}
                        >
                          {variant.sku}
                        </TableCell>

                        {/* Location Inventory Cells */}
                        {locations.map((location) => {
                          const inv = variantInventory.get(location.id);
                          const quantity = inv?.quantity || 0;
                          const reserved = inv?.reserved || 0;
                          const allocated = inv?.allocated || 0;
                          const available = quantity - reserved - allocated;
                          const threshold = inv?.lowStockThreshold || 10;

                          return (
                            <TableCell
                              key={location.id}
                              align="center"
                              sx={{
                                borderBottom: `1px solid ${colors.borderLight}`,
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: alpha(palette.primary, 0.1),
                                },
                              }}
                              onClick={() => handleCellClick(variant.id, location.id)}
                            >
                              {inv ? (
                                <Tooltip
                                  title={
                                    <Box sx={{ p: 0.5 }}>
                                      <Typography variant="caption" sx={{ display: 'block' }}>
                                        Total: {quantity}
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block' }}>
                                        Available: {available}
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block' }}>
                                        Reserved: {reserved}
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block' }}>
                                        Allocated: {allocated}
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                        Click to adjust
                                      </Typography>
                                    </Box>
                                  }
                                  arrow
                                >
                                  <Box>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontFamily: typography.fontFamily.mono,
                                        fontWeight: typography.fontWeight.extrabold,
                                        color: getStockColor(available, threshold),
                                      }}
                                    >
                                      {available}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: typography.fontFamily.mono,
                                        fontSize: typography.fontSize.xs,
                                        color: colors.textMuted,
                                      }}
                                    >
                                      / {quantity}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Click to add inventory" arrow>
                                  <IconButton
                                    size="small"
                                    sx={{
                                      color: colors.textMuted,
                                      '&:hover': {
                                        color: palette.primary,
                                      },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Stock Adjustment Dialog */}
      {selectedVariantId && selectedLocationId && (
        <QuickStockAdjustmentDialog
          open={stockDialogOpen}
          onClose={handleCloseDialog}
          productId={selectedVariantId}
          preselectedLocationId={selectedLocationId}
        />
      )}
    </>
  );
};
